import React, { useState } from 'react';
import { TrackerInfo } from '../utils/types';
import { T, TRACKER_CATEGORY } from '../designTokens';

interface TrackerTableProps {
  trackers: TrackerInfo[];
  initialFilter?: string;
}

const resourceTypeLabels: Record<string, { label: string; description: string }> = {
  script: { label: 'JavaScript', description: 'Loaded as a JavaScript file' },
  stylesheet: { label: 'CSS', description: 'Loaded as a stylesheet' },
  image: { label: 'Image', description: 'Loaded as an image (tracking pixel)' },
  font: { label: 'Font', description: 'Loaded as a web font' },
  xhr: { label: 'XHR Request', description: 'Background data request (XMLHttpRequest)' },
  fetch: { label: 'Fetch Request', description: 'Background data request (Fetch API)' },
  ping: { label: 'Tracking Beacon', description: 'Analytics/tracking beacon' },
  websocket: { label: 'WebSocket', description: 'Real-time connection' },
  media: { label: 'Media', description: 'Audio or video content' },
  document: { label: 'Document', description: 'HTML document' },
  iframe: { label: 'Embedded Frame', description: 'Loaded in an iframe' },
  other: { label: 'Other', description: 'Other resource type' },
};

export default function TrackerTable({ trackers, initialFilter = '' }: TrackerTableProps) {
  const [searchTerm, setSearchTerm] = useState(initialFilter);
  if (trackers.length === 0) {
    return (
      <div style={{ padding: 20 }}>
        <h3 style={{ marginBottom: 16, color: T.gray800, fontFamily: T.fontBody, fontWeight: 700, fontSize: 16 }}>
          Third-Party Trackers
        </h3>
        <p style={{ color: T.gray500 }}>No third-party trackers detected.</p>
      </div>
    );
  }

  const sorted = [...trackers].sort((a, b) => {
    const order: Record<string, number> = {
      marketing: 0, social_media: 1, analytics: 2, functional: 3, strictly_necessary: 4, unknown: 5,
    };
    return (order[a.category] ?? 5) - (order[b.category] ?? 5);
  });

  const filteredTrackers = searchTerm 
    ? sorted.filter(t => 
        t.domain.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (t.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : sorted;

  return (
    <div style={{ padding: 20 }}>
      <h3 style={{ marginBottom: 16, color: T.gray800, fontFamily: T.fontBody, fontWeight: 700, fontSize: 16 }}>
        Third-Party Trackers ({trackers.length})
      </h3>
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          backgroundColor: T.infoBg,
          border: `1px solid ${T.infoBorder}`,
          borderRadius: T.radiusMd,
          fontSize: 13,
          color: T.gray600,
        }}
      >
        <strong>How to validate:</strong> Open your browser&apos;s Developer Tools (F12), go to the Network tab,
        and reload the page. You&apos;ll see all requests including trackers.
      </div>
      <div style={{ marginBottom: 16 }}>
        <input 
          type="text" 
          placeholder="Filter trackers by name or domain..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', border: `1px solid ${T.gray300}`, borderRadius: 6, fontFamily: T.fontBody, fontSize: 14 }}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ backgroundColor: T.gray50 }}>
              <th style={thStyle}>Domain</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Loading Method</th>
              <th style={thStyle}>
                <span title="Indicates if this tracker category requires opt-out controls under CCPA">
                  Needs Opt-Out
                </span>
              </th>
              <th style={thStyle}>
                <span title="Whether this tracker was blocked when Global Privacy Control (GPC) signal was sent">
                  GPC Honored
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredTrackers.map((tracker, i) => {
              const cat = TRACKER_CATEGORY[tracker.category] ?? TRACKER_CATEGORY.unknown;
              return (
                <tr
                  key={tracker.domain}
                  style={{
                    backgroundColor: i % 2 === 0 ? T.white : T.gray50,
                    borderBottom: `1px solid ${T.gray200}`,
                  }}
                >
                  <td style={tdStyle}>
                    <code style={{ fontSize: 13, fontFamily: T.fontMono, color: T.gray700 }}>
                      {tracker.domain}
                    </code>
                    {tracker.sameCompanyOwner && (
                      <div
                        style={{ fontSize: 12, color: T.gray500, marginTop: 2 }}
                        title="Run by the same company as this site, so not a third party under CCPA"
                      >
                        Same company ({tracker.sameCompanyOwner})
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>{tracker.name || '—'}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        backgroundColor: cat.bg,
                        color: cat.color,
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      {cat.label}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {tracker.resourceType || tracker.loadContext || tracker.initiator ? (
                      <div style={{ fontSize: 13, color: T.gray500 }}>
                        {tracker.resourceType && (
                          <div
                            title={resourceTypeLabels[tracker.resourceType]?.description || `Resource type: ${tracker.resourceType}`}
                            style={{ cursor: 'help' }}
                          >
                            <span style={{ fontWeight: 500 }}>Type:</span>{' '}
                            <span style={{ color: T.cobalt600 }}>
                              {resourceTypeLabels[tracker.resourceType]?.label || tracker.resourceType}
                            </span>
                          </div>
                        )}
                        {tracker.loadContext === 'iframe' && (
                          <div style={{ color: T.warning, fontWeight: 500 }} title="Loaded inside an iframe">
                            Loaded via iframe
                          </div>
                        )}
                        {tracker.initiator && (
                          <div style={{ fontSize: 12, color: T.gray400 }} title={`Initiated by: ${tracker.initiator}`}>
                            by {tracker.initiator}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: T.gray300 }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {!tracker.isCompliant ? (
                      <span style={{ color: T.gray600 }} title="Requires opt-out under CCPA">Yes</span>
                    ) : (
                      <span style={{ color: T.gray400 }}>No</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {tracker.blockedByGPC === undefined ? (
                      <span style={{ color: T.gray300 }}>—</span>
                    ) : tracker.blockedByGPC ? (
                      <span style={{ color: T.success, fontWeight: 600 }}>Blocked</span>
                    ) : (
                      <span style={{ color: T.error, fontWeight: 600 }}>Not Blocked</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px',
  fontWeight: 600,
  color: T.gray500,
  borderBottom: `2px solid ${T.gray200}`,
  fontSize: 12,
  fontFamily: T.fontBody,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: T.gray700,
};
